/**
 * 당구 교실 — the tutorial curriculum.
 *
 * Pure data + pure predicates: a mission knows what the table should look like,
 * what the player has to do, and how to tell (from the shot that just finished,
 * or from a UI action) that they did it. Nothing here touches the DOM, Three.js
 * or the App, so the whole curriculum is unit-testable and the App only has to
 * place balls, ask "did this satisfy the current mission?", and advance.
 *
 * Two mission kinds:
 *  - `actions`  — completed by USING a control (press 1/2, G, C…). These teach
 *                 the controls that aren't a shot, and can't be failed.
 *  - `check`    — completed by a SHOT that satisfies a predicate. A shot that
 *                 doesn't is never a failure: the App just resets the layout and
 *                 the panel says "try again" (see `retryLayout`).
 *
 * Copy lives in i18n under `tut.<id>.title` / `.goal` / `.tip`, so both languages
 * stay in one place and a mission carries no English in this file.
 */
import { ballRadius, tableFor, type BallId, type VariantId } from '../config';
import type { CueParams, GameEvent } from '../types';
import type { ShotEvaluation } from '../rules/types';

/**
 * A control the player can exercise without taking a shot.
 *
 * `aim:*` and `view:orbit` are mouse-and-keyboard ideas: on desktop the game has
 * two modes (aim mode, where the mouse moves the cue, and view mode, where it
 * moves the camera) and knowing how to get between them is the first thing a
 * player needs. Touch has no such split — gestures aim the cue directly — so the
 * mission that teaches them is `desktopOnly`.
 */
export type TutorialAction =
  | 'aim:enter' | 'aim:exitkey' | 'view:orbit' | 'guide' | 'chalk'
  | 'spin:drag' | 'view:zoom' | 'aim:fine'
  | 'touch:aim' | 'touch:orbit' | 'touch:zoom';

/** Everything a shot-mission predicate is allowed to look at. */
export interface ShotContext {
  /** The parameters the shot was actually fired with (aim, speed, tip, tilt). */
  cue: CueParams;
  /** The rules engine's verdict on the shot. */
  evaluation: ShotEvaluation;
  /** The full contact log, for things the evaluation doesn't summarize. */
  events: readonly GameEvent[];
  variant: VariantId;
}

/**
 * Ball placement in NORMALIZED table coordinates: x and y each run -1..1 across
 * the play surface (1 = touching the cushion nose). Normalized rather than
 * absolute so a layout stays legal whatever table its variant is played on (the
 * 중대 and the match table differ by ~200 mm) — see `resolveLayout`.
 */
export type NormalizedLayout = Partial<Record<BallId, { x: number; y: number }>>;

export interface Mission {
  id: string;
  /** Which game the mission is played in; the App rebuilds the table to match. */
  variant: VariantId;
  /** Chapter this mission belongs to (for the panel's progress list). */
  chapter: ChapterId;
  /** Where the balls start. Omitted balls keep their standard opening spot. */
  layout: NormalizedLayout;
  /**
   * Action mission: completed once EVERY listed action has been performed (in
   * any order). Mutually exclusive with `check`.
   */
  actions?: readonly TutorialAction[];
  /** Shot mission: completed by a shot satisfying this. */
  check?: (ctx: ShotContext) => boolean;
  /**
   * Shot missions only: re-place the layout after a shot that didn't satisfy the
   * goal, so the player always retries from the same picture. Off for missions
   * where wherever the balls stopped is just as good a starting point.
   */
  retryLayout?: boolean;
  /**
   * Teaches something that only exists with a mouse and keyboard. Dropped from
   * the curriculum on touch devices rather than shown as an impossible step —
   * see `missionsFor`.
   */
  desktopOnly?: boolean;
  /**
   * Teaches something that only exists on a touch screen (a gesture with no
   * mouse-and-keyboard equivalent). Dropped from the curriculum on desktop —
   * the mirror image of `desktopOnly` — see `missionsFor`.
   */
  touchOnly?: boolean;
  /**
   * The intended ball path, as a polyline in the same normalized coordinates as
   * `layout` (2+ points). Drives BOTH the mini diagram in the tutorial panel and
   * the animated arrow drawn over the real table — one set of numbers, so the
   * two can never disagree about what the lesson is asking for. Action missions
   * and free-form ones (e.g. `stroke`) can omit it.
   */
  hint?: readonly { x: number; y: number }[];
  /**
   * Starting cue-ball aim angle override, in radians (world azimuth, same
   * convention as `App.azimuth`). Unset = auto-face the nearest object ball
   * (the default for every built-in mission — see `App.faceObjectBall`).
   */
  cueAngle?: number;
}

export type ChapterId = 'basics' | 'aim' | 'spin' | 'play';

export const CHAPTERS: readonly ChapterId[] = ['basics', 'aim', 'spin', 'play'];

/**
 * A tutorial is now three separately-startable courses rather than one long
 * curriculum: a player who already knows the controls can jump straight to
 * skills, and "done" means done with THAT course, not all twelve missions.
 */
export type CourseId = 'control' | 'skills' | 'advanced';

export interface CourseMeta {
  id: CourseId;
  /** Which chapters belong to this course, in play order. */
  chapters: readonly ChapterId[];
  icon: string;
}

export const COURSES: readonly CourseMeta[] = [
  { id: 'control', chapters: ['basics'], icon: '🕹️' },
  { id: 'skills', chapters: ['aim', 'spin'], icon: '🎯' },
  { id: 'advanced', chapters: ['play'], icon: '🏆' },
];

/** Which course a mission's chapter belongs to. */
export function courseOf(mission: Mission): CourseId {
  return COURSES.find((c) => c.chapters.includes(mission.chapter))!.id;
}

// ---- thresholds -------------------------------------------------------------
// Deliberately loose. These gate "did you understand the control", not "are you
// any good with it" — a player who half-commits to the idea should still pass.

/** Cue speed (m/s) a "soft touch" must stay under. Full power is ~7 m/s. */
export const SOFT_SPEED = 1.8;
/** Tip offset (fraction of the ball radius) that counts as a deliberate spin. */
export const SPIN_TIP = 0.45;
/** Cue elevation (radians) that counts as a deliberate massé tilt (~20°). */
export const MASSE_TILT = 0.35;
/** Cushions the cue ball must find in the power drill. */
export const POWER_CUSHIONS = 3;

// ---- predicate helpers ------------------------------------------------------

/** True when the shot's first contact was one of the (red) object balls. */
export function hitObjectBall(ctx: ShotContext): boolean {
  const first = ctx.evaluation.firstObjectBall;
  return first === 'red' || first === 'red2';
}

/** How many cushions the CUE ball touched over the whole shot. */
export function cueCushions(ctx: ShotContext): number {
  return ctx.events.filter((e) => e.kind === 'ball-cushion' && e.ball === ctx.cue.ball).length;
}

// ---- the curriculum ---------------------------------------------------------

/**
 * The missions, in the order they're played. Ordering is the teaching: a control
 * is never needed by a mission before the mission that introduces it.
 *
 * Layout convention: the cue ball sits low (-y, the head of the table) so the
 * default aim camera looks up the table, and object balls are placed to make the
 * intended shot the obvious one.
 */
export const MISSIONS: readonly Mission[] = [
  // ---- Chapter 1: the basics ------------------------------------------------
  {
    // Before anything else: the game has two modes, and every other lesson
    // assumes you can get between them. Left-click puts the mouse on the cue,
    // right-drag puts it on the camera, and any key hands the cursor back — which
    // is also the escape hatch when the controls "stop responding".
    id: 'aimmode',
    variant: 'straight-rail',
    chapter: 'basics',
    desktopOnly: true,
    layout: { white: { x: 0, y: -0.5 }, yellow: { x: -0.55, y: -0.85 }, red: { x: 0, y: 0.5 }, red2: { x: 0.3, y: 0.1 } },
    actions: ['aim:enter', 'aim:exitkey', 'view:orbit'],
  },
  {
    // Touch's mirror of `aimmode`: gestures aim the cue directly, so there's no
    // mode to switch — but nothing else teaches the camera gestures either, and
    // the very next mission expects the player to already know how to aim.
    id: 'touchaim',
    variant: 'straight-rail',
    chapter: 'basics',
    touchOnly: true,
    layout: { white: { x: 0, y: -0.5 }, yellow: { x: -0.55, y: -0.85 }, red: { x: 0, y: 0.5 }, red2: { x: 0.3, y: 0.1 } },
    actions: ['touch:aim', 'touch:orbit', 'touch:zoom'],
  },
  {
    // Anything at all counts. The point is only to discover that a shot is
    // "pull back, then push forward" — landing it somewhere is a bonus.
    id: 'stroke',
    variant: 'straight-rail',
    chapter: 'basics',
    layout: { white: { x: 0, y: -0.5 }, yellow: { x: -0.55, y: -0.85 }, red: { x: 0, y: 0.5 }, red2: { x: 0.3, y: 0.1 } },
    check: () => true,
    retryLayout: true,
    hint: [{ x: 0, y: -0.5 }, { x: 0, y: -0.1 }],
  },
  {
    id: 'guide',
    variant: 'straight-rail',
    chapter: 'basics',
    layout: { white: { x: 0, y: -0.5 }, yellow: { x: -0.55, y: -0.85 }, red: { x: 0, y: 0.5 }, red2: { x: 0.3, y: 0.1 } },
    actions: ['guide'],
  },
  {
    // The rest of the mouse's jobs in aim mode: drag to set English, roll the
    // wheel to reframe the shot, and hold Shift for a slower, more precise turn.
    id: 'mousecontrols',
    variant: 'straight-rail',
    chapter: 'basics',
    desktopOnly: true,
    layout: { white: { x: 0, y: -0.5 }, yellow: { x: -0.55, y: -0.85 }, red: { x: 0, y: 0.5 }, red2: { x: 0.3, y: 0.1 } },
    actions: ['spin:drag', 'view:zoom', 'aim:fine'],
  },

  // ---- Chapter 2: aim and power --------------------------------------------
  {
    // A red straight up the table, the other red parked out of the way, so the
    // only thing being tested is "can you point the cue at a ball".
    id: 'aim',
    variant: 'straight-rail',
    chapter: 'aim',
    layout: { white: { x: 0, y: -0.6 }, yellow: { x: -0.8, y: -0.9 }, red: { x: 0, y: 0.35 }, red2: { x: 0.8, y: 0.9 } },
    check: hitObjectBall,
    retryLayout: true,
    hint: [{ x: 0, y: -0.6 }, { x: 0, y: 0.35 }],
  },
  {
    // Same picture, but it has to arrive gently — the stroke is a speed, not a
    // button, and this is where that lands.
    id: 'soft',
    variant: 'straight-rail',
    chapter: 'aim',
    layout: { white: { x: 0, y: -0.35 }, yellow: { x: -0.8, y: -0.9 }, red: { x: 0, y: 0.2 }, red2: { x: 0.8, y: 0.9 } },
    check: (ctx) => hitObjectBall(ctx) && ctx.cue.speed <= SOFT_SPEED,
    retryLayout: true,
    hint: [{ x: 0, y: -0.35 }, { x: 0, y: 0.2 }],
  },
  // ---- Chapter 3: spin ------------------------------------------------------
  {
    id: 'chalk',
    variant: 'straight-rail',
    chapter: 'spin',
    layout: { white: { x: 0, y: -0.55 }, yellow: { x: -0.8, y: -0.9 }, red: { x: 0, y: 0.3 }, red2: { x: 0.5, y: 0.7 } },
    actions: ['chalk'],
  },
  {
    // Side English only shows itself off a rail, so the mission asks for both:
    // put the tip off-centre AND send the ball into a cushion to watch it bite.
    id: 'side',
    variant: 'straight-rail',
    chapter: 'spin',
    layout: { white: { x: 0, y: -0.5 }, yellow: { x: -0.85, y: -0.92 }, red: { x: 0.85, y: 0.92 }, red2: { x: 0.6, y: 0.92 } },
    check: (ctx) => Math.abs(ctx.cue.tipOffset.a) >= SPIN_TIP && cueCushions(ctx) >= 1,
    retryLayout: true,
    // A bend off the side rail, to sell "the rebound angle changes" over "hit it".
    hint: [{ x: 0, y: -0.5 }, { x: 0.85, y: 0.55 }, { x: 0.55, y: 0.92 }],
  },
  {
    id: 'draw',
    variant: 'straight-rail',
    chapter: 'spin',
    layout: { white: { x: 0, y: -0.45 }, yellow: { x: -0.8, y: -0.9 }, red: { x: 0, y: 0.1 }, red2: { x: 0.8, y: 0.9 } },
    check: (ctx) => hitObjectBall(ctx) && ctx.cue.tipOffset.b <= -SPIN_TIP,
    retryLayout: true,
    // Out to the red, then back toward the player — the whole point of draw.
    hint: [{ x: 0, y: -0.45 }, { x: 0, y: 0.1 }, { x: 0, y: -0.2 }],
  },
  {
    id: 'follow',
    variant: 'straight-rail',
    chapter: 'spin',
    layout: { white: { x: 0, y: -0.45 }, yellow: { x: -0.8, y: -0.9 }, red: { x: 0, y: 0.1 }, red2: { x: 0.8, y: 0.9 } },
    check: (ctx) => hitObjectBall(ctx) && ctx.cue.tipOffset.b >= SPIN_TIP,
    retryLayout: true,
    // Straight through the red and on, to sell "the white keeps travelling".
    hint: [{ x: 0, y: -0.45 }, { x: 0, y: 0.1 }, { x: 0, y: 0.55 }],
  },
  // ---- Chapter 4: real shots ------------------------------------------------
  {
    // 빈쿠션 — rail first, then the ball. The cue ball starts nearly square to
    // the short rail with the reds up-table, so the bank is there to be seen.
    id: 'bank',
    variant: 'straight-rail',
    chapter: 'play',
    layout: { white: { x: -0.5, y: -0.7 }, yellow: { x: -0.85, y: -0.95 }, red: { x: -0.4, y: 0.45 }, red2: { x: 0.1, y: 0.6 } },
    check: (ctx) => hitObjectBall(ctx) && ctx.evaluation.cushionsBeforeFirst >= 1,
    retryLayout: true,
    hint: [{ x: -0.5, y: -0.7 }, { x: -0.9, y: -0.15 }, { x: -0.4, y: 0.45 }],
  },
  {
    // The real thing, from a layout that all but plays itself: both reds sit
    // just past the cue ball at a shallow angle.
    id: 'carom',
    variant: 'straight-rail',
    chapter: 'play',
    layout: { white: { x: -0.15, y: -0.45 }, yellow: { x: -0.85, y: -0.95 }, red: { x: 0.05, y: -0.05 }, red2: { x: 0.3, y: 0.25 } },
    check: (ctx) => ctx.evaluation.scored,
    retryLayout: true,
    hint: [{ x: -0.15, y: -0.45 }, { x: 0.05, y: -0.05 }, { x: 0.3, y: 0.25 }],
  },
];

/**
 * The curriculum for this device. Touch drops the mouse-and-keyboard-only
 * lessons instead of showing a step that cannot be completed — everything else,
 * including the ordering, is identical.
 */
export function missionsFor(touch: boolean): readonly Mission[] {
  return MISSIONS.filter((m) => (touch ? !m.desktopOnly : !m.touchOnly));
}

/** The curriculum for one course, on this device — see `missionsFor`. */
export function missionsForCourse(course: CourseId, touch: boolean): readonly Mission[] {
  const chapters = COURSES.find((c) => c.id === course)!.chapters;
  return missionsFor(touch).filter((m) => chapters.includes(m.chapter));
}

/** Look a mission up by id (null when unknown — e.g. saved progress went stale). */
export function missionById(id: string): Mission | null {
  return MISSIONS.find((m) => m.id === id) ?? null;
}

/**
 * Turn a mission's normalized layout into real table coordinates (metres) for
 * its variant, keeping every ball inside the cushion noses by its own radius.
 */
export function resolveLayout(mission: Mission): Partial<Record<BallId, { x: number; y: number }>> {
  const table = tableFor(mission.variant);
  const r = ballRadius(mission.variant);
  const hx = Math.max(0, table.playWidth / 2 - r);
  const hy = Math.max(0, table.playLength / 2 - r);
  const out: Partial<Record<BallId, { x: number; y: number }>> = {};
  for (const [id, p] of Object.entries(mission.layout)) {
    if (!p) continue;
    out[id as BallId] = {
      x: Math.max(-hx, Math.min(hx, p.x * hx)),
      y: Math.max(-hy, Math.min(hy, p.y * hy)),
    };
  }
  return out;
}

/**
 * The mission's hint polyline in real table coordinates (metres), clamped the
 * same way `resolveLayout` clamps ball positions. Null when the mission has no
 * hint (action missions, and free-form ones like `stroke`).
 */
export function resolveHint(mission: Mission): readonly { x: number; y: number }[] | null {
  if (!mission.hint || mission.hint.length < 2) return null;
  const table = tableFor(mission.variant);
  const r = ballRadius(mission.variant);
  const hx = Math.max(0, table.playWidth / 2 - r);
  const hy = Math.max(0, table.playLength / 2 - r);
  return mission.hint.map((p) => ({
    x: Math.max(-hx, Math.min(hx, p.x * hx)),
    y: Math.max(-hy, Math.min(hy, p.y * hy)),
  }));
}

/**
 * Has an action mission been satisfied? `done` is the set of actions performed
 * since the mission started; every action the mission lists must appear.
 */
export function actionsSatisfied(mission: Mission, done: ReadonlySet<TutorialAction>): boolean {
  if (!mission.actions) return false;
  return mission.actions.every((a) => done.has(a));
}

/** Did this shot complete the mission? False for action missions. */
export function shotSatisfies(mission: Mission, ctx: ShotContext): boolean {
  return mission.check ? mission.check(ctx) : false;
}
